import { LayerModel } from "./LayerModel";
import { ObservableModelNode } from "./observable";
import { SPECS_DISPLAY_METHODS } from "./displayMethods";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readObjectArray,
  readOptionalNumber,
  readString,
  readStringEnum,
  readStringArray,
  readUnknownArray,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";
import type { SpecsDisplayMethod } from "../types";

export type AddLayerOptions = Partial<{
  layerLabel: string;
  fabricNames: string[];
  required: boolean;
  credits: number;
  excludeFromColorCount: boolean;
  displayable: boolean;
  displayGroup: string;
  displayMethod: SpecsDisplayMethod;
  menuIconUrl: string;
}>;

export class StyleModel extends ObservableModelNode {
  name!: string;
  defaultLayer!: string;
  positionIds!: string[];
  layers!: LayerModel[];
  extra!: JsonObject;
  displayExtra!: JsonObject;
  displayable!: boolean;
  menuIconUrl!: string;
  displayMethod!: SpecsDisplayMethod;
  credits!: number;
  disabled!: boolean;
  disablingWhenSelected!: string[];
  silentMigration!: unknown[];
  migrateTo!: string;
  useColorCombo!: boolean;
  colorCombos!: unknown[];
  preventColorDuplicates!: boolean;
  pattern!: string;
  patternTextColor!: string;
  private hasCredits = false;
  private hasDisabled = false;
  private hasUseColorCombo = false;
  private hasPreventColorDuplicates = false;
  private hasDisplayMethod = false;
  private hasRawSilentMigration = false;
  private rawSilentMigration: unknown = undefined;
  private hasRawDisablingWhenSelected = false;
  private rawDisablingWhenSelected: unknown[] = [];

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("defaultLayer", "");
    this.defineObservableValue("positionIds", []);
    this.defineObservableValue("layers", []);
    this.defineObservableValue("extra", {});
    this.defineObservableValue("displayExtra", {});
    this.defineObservableValue("displayable", true);
    this.defineObservableValue("menuIconUrl", "");
    this.defineObservableValue("displayMethod", "icon");
    this.defineObservableValue("credits", 0);
    this.defineObservableValue("disabled", false);
    this.defineObservableValue("disablingWhenSelected", []);
    this.defineObservableValue("silentMigration", []);
    this.defineObservableValue("migrateTo", "");
    this.defineObservableValue("useColorCombo", false);
    this.defineObservableValue("colorCombos", []);
    this.defineObservableValue("preventColorDuplicates", false);
    this.defineObservableValue("pattern", "");
    this.defineObservableValue("patternTextColor", "");
  }

  restore(data: unknown) {
    const source = expectObject(data, "style");
    const display = source.display
      ? expectObject(source.display, "style.display")
      : {};

    this.hasCredits = "credits" in source;
    this.hasDisabled = "disabled" in source;
    this.hasUseColorCombo = "useColorCombo" in source;
    this.hasPreventColorDuplicates = "preventColorDuplicates" in source;
    this.hasDisplayMethod = "displayMethod" in display;
    this.hasRawDisablingWhenSelected = Array.isArray(
      source.disablingWhenSelected
    );
    this.rawDisablingWhenSelected = this.hasRawDisablingWhenSelected
      ? cloneValue(source.disablingWhenSelected as unknown[])
      : [];
    this.hasRawSilentMigration =
      "silentMigration" in source && !Array.isArray(source.silentMigration);
    this.rawSilentMigration =
      this.hasRawSilentMigration && source.silentMigration !== undefined
        ? cloneValue(source.silentMigration)
        : source.silentMigration;
    this.extra = splitKnown(source, [
      "name",
      "credits",
      "defaultLayer",
      "positionIds",
      "layers",
      "display",
      "disabled",
      "disablingWhenSelected",
      "silentMigration",
      "migrateTo",
      "useColorCombo",
      "colorCombos",
      "preventColorDuplicates",
      "pattern",
      "patternTextColor",
    ]);
    this.displayExtra = splitKnown(display, [
      "displayable",
      "menuIconUrl",
      "displayMethod",
    ]);
    this.name = readString(source, "name");
    this.credits = readOptionalNumber(source, "credits") ?? 0;
    this.defaultLayer = readString(source, "defaultLayer");
    this.positionIds = cloneValue(readStringArray(source, "positionIds"));
    this.displayable = readBoolean(display, "displayable", true);
    this.menuIconUrl = readString(display, "menuIconUrl");
    this.displayMethod = readStringEnum(
      display,
      "displayMethod",
      SPECS_DISPLAY_METHODS,
      "icon"
    );
    this.disabled = readBoolean(source, "disabled", false);
    this.disablingWhenSelected = cloneValue(
      readStringArray(source, "disablingWhenSelected")
    );
    this.silentMigration = readUnknownArray(source, "silentMigration");
    this.migrateTo = readString(source, "migrateTo");
    this.useColorCombo = readBoolean(source, "useColorCombo", false);
    this.colorCombos = readUnknownArray(source, "colorCombos");
    this.preventColorDuplicates = readBoolean(
      source,
      "preventColorDuplicates",
      false
    );
    this.pattern = readString(source, "pattern");
    this.patternTextColor = readString(source, "patternTextColor");
    this.layers = readObjectArray(source, "layers").map((layer) =>
      new LayerModel().restore(layer)
    );

    return this;
  }

  save() {
    return {
      ...cloneValue(this.extra),
      name: this.name,
      ...(this.hasCredits || this.credits ? { credits: this.credits } : {}),
      defaultLayer: this.defaultLayer,
      positionIds: cloneValue(this.positionIds),
      ...(this.hasDisabled || this.disabled ? { disabled: this.disabled } : {}),
      disablingWhenSelected: this.saveDisablingWhenSelected(),
      ...this.saveSilentMigration(),
      ...(this.migrateTo ? { migrateTo: this.migrateTo } : {}),
      ...(this.hasUseColorCombo || this.useColorCombo
        ? { useColorCombo: this.useColorCombo }
        : {}),
      ...(this.colorCombos.length > 0
        ? { colorCombos: cloneValue(this.colorCombos) }
        : {}),
      ...(this.hasPreventColorDuplicates || this.preventColorDuplicates
        ? { preventColorDuplicates: this.preventColorDuplicates }
        : {}),
      ...(this.pattern ? { pattern: this.pattern } : {}),
      ...(this.patternTextColor
        ? { patternTextColor: this.patternTextColor }
        : {}),
      layers: this.layers.map((layer) => layer.save()),
      display: {
        ...cloneValue(this.displayExtra),
        displayable: this.displayable,
        menuIconUrl: this.menuIconUrl,
        ...(this.hasDisplayMethod || this.displayMethod !== "icon"
          ? { displayMethod: this.displayMethod }
          : {}),
      },
    };
  }

  private saveSilentMigration() {
    if (this.silentMigration.length > 0) {
      return { silentMigration: cloneValue(this.silentMigration) };
    }

    if (this.hasRawSilentMigration) {
      return {
        silentMigration:
          this.rawSilentMigration === undefined
            ? undefined
            : cloneValue(this.rawSilentMigration),
      };
    }

    return {};
  }

  private saveDisablingWhenSelected() {
    if (this.disablingWhenSelected.length > 0) {
      return cloneValue(this.disablingWhenSelected);
    }

    if (this.hasRawDisablingWhenSelected) {
      return cloneValue(this.rawDisablingWhenSelected);
    }

    return [];
  }

  getLayer(name?: string | null) {
    return (
      this.layers.find((layer) => layer.name === (name || "")) ??
      this.layers[0] ??
      null
    );
  }

  addLayer(name: string, options: AddLayerOptions = {}) {
    const layerName = name.trim();
    if (!layerName || this.layers.some((layer) => layer.name === layerName)) {
      return null;
    }

    const layer = new LayerModel();
    layer.name = layerName;
    layer.layerLabel = options.layerLabel || layerName;
    layer.fabricNames = cloneValue(options.fabricNames || []);
    layer.required = options.required || true;
    layer.credits = options.credits || 0;
    layer.excludeFromColorCount = options.excludeFromColorCount || false;
    layer.displayable = options.displayable || true;
    layer.displayGroup = options.displayGroup || "main";
    layer.displayMethod = options.displayMethod || "colorPalette";
    layer.displayExtra = {
      ...(options.menuIconUrl !== undefined
        ? { menuIconUrl: options.menuIconUrl }
        : {}),
      displayMethod: layer.displayMethod,
    };

    this.layers.push(layer);
    if (!this.defaultLayer) {
      this.defaultLayer = layer.name;
    }
    return layer;
  }

  getDefaultLayer() {
    return this.getLayer(this.defaultLayer);
  }

  getInheritanceSourceLayer(inheritFromGroup: string | null) {
    if (!inheritFromGroup) {
      return this.getDefaultLayer();
    }

    return (
      this.layers.find(
        (layer) => layer.parentOfInheritanceGroup === inheritFromGroup
      ) ?? this.getDefaultLayer()
    );
  }

  getInheritanceGroups() {
    return this.layers
      .map((layer) => layer.parentOfInheritanceGroup)
      .filter((groupName): groupName is string => !!groupName);
  }
}
